/**
 * Registr kódů chyb API — JEDINÝ zdroj kódů a jejich anglických šablon (vize C15 / F13.9).
 *
 * Chybová hláška katalogové části adminu nejde po drátě jako text, ale jako KÓD: horní chyba
 * v `error` (`{ error: "destination_parent_self", message, params? }`), chyba u pole jako
 * hodnota v `details.fieldErrors` (`{ slug: ["slug_format"] }`). Admin kód přeloží svým
 * slovníkem (`apps/admin/src/i18n/{en,cs}/apiErrors.ts`, typovaný přes `ApiErrorCode`), takže
 * v českém adminu je hláška česky a v anglickém anglicky. Anglický `message` z šablony tady
 * zůstává pro logy, skripty a jiné klienty.
 *
 * Proč ne překlad podle textu (`ZOD_MESSAGE_MAP` v adminu): regex nad anglickou větou se
 * rozejde s první přeformulací a nový text tiše propadne nepřeložený. Kód je smlouva, kterou
 * hlídá typecheck (slovník) i měřidlo `scripts/kontrola/chybove-kody.mjs` (routy, schémata).
 *
 * Konvence: `snake_case`, tvar `<oblast>_<co>`. Parametry šablon `{{jmeno}}` mají syntaxi
 * i18next. Čísla v šablonách číslovku neskloňují („počet: {{places}}"), protože klíč slovníku
 * je přesně kód — plurálové přípony `_one/_few` tu nejsou.
 *
 * Kód NIKDY nejde na web: veřejné formuláře (poptávky) mají vlastní české texty a slovník
 * adminu nemají.
 */
export const API_ERROR_MESSAGES = {
  // ── Hierarchie destinací (vize F13.9 — pět hlášek) ──
  destination_parent_self: "A destination cannot be its own parent destination.",
  destination_parent_not_found:
    "The selected parent destination no longer exists. Reload the page and choose another one.",
  destination_parent_not_country_level:
    'The parent destination must be a country, or a place with "Show as a country" turned on.',
  destination_with_places_needs_show_as_country:
    'This destination has its own places, so it can only sit under another destination when "Show as a country" is turned on. Turn it on, or move its places first.',
  destination_with_places_needs_top_level_parent:
    "A destination with its own places must sit directly under a country — the hierarchy has at most three levels.",
  destination_in_use:
    'Destination "{{name}}" cannot be deleted because places (count: {{places}}) and trips (count: {{trips}}) depend on it. Move the places and detach the trips first.',

  // ── Slugy a unikátní hodnoty ──
  slug_format:
    "The slug may contain only lowercase letters without diacritics, digits and hyphens.",
  slug_reserved:
    "This slug is reserved for a trip type or a destination subpage ({{slugs}}). Choose another one.",
  value_taken: "This value is already in use. Please choose a different one.",

  // ── Webové adresy (vize F5.2 — odkaz na další videa destinace) ──
  url_https_required: "Enter a full web address starting with https://.",

  // ── Zájezd (hotel, termíny, pořadí) ──
  hotel_coordinates_pair: "Hotel coordinates need both latitude and longitude.",
  hotel_room_ids_unique: "Room IDs must be unique.",
  hotel_meal_plans_unique: "Each meal plan can be offered only once.",
  hotel_departures_unique: "Departure places must be unique.",
  trip_experience_field_required: "Required for an experience.",
  trip_tour_field_required: "Required for a tour.",
  trip_reorder_invalid:
    "The new trip order could not be saved — the list is invalid. Reload the page and try again.",
  departure_capacity_required:
    "Capacity is required — this trip has no upper group-size limit to derive it from.",
  departure_capacity_invalid: "Capacity must be a number (0 or more).",

  // ── Týmy ──
  team_member_name_required: "Member name is required.",

  // ── Souběžná úprava ──
  conflict: "This record was changed by someone else ({{when}}) since you opened it.",

  // ── Média ──
  media_alt_required: "Alt text is required for SEO and accessibility.",
  media_no_file: "No file was provided.",
  media_alts_mismatch: "The number of alt texts does not match the number of files.",
  media_fields_missing: "Select the media and the target folder.",
  media_folder_invalid: "The selected folder does not exist.",
  media_no_ids: "No media selected.",
  media_too_large: "{{file}}: the file is too large. Maximum size is {{maxMb}} MB.",
  media_type_not_allowed: "{{file}}: this file type is not allowed. Allowed types: {{types}}.",
  media_svg_unsafe: "{{file}}: the SVG contains potentially unsafe content.",
  media_content_invalid: "{{file}}: the file content does not match a valid image format.",
  media_mime_mismatch: "{{file}}: the file content does not match its declared type.",
  media_exceeds_image_limits:
    "{{file}}: the photo is larger than processing allows (at most {{maxMb}} MB, {{maxPx}} px per side and {{maxMp}} Mpx).",
  media_upload_failed: "Upload failed: {{reason}}",
  media_ai_failed: "AI suggestion failed: {{reason}}",
  media_restore_live_twin: "A live photo with the same content already exists.",
  media_country_not_country_level:
    'A photo country must be a country, or a place with "Show as a country" turned on.',
  guide_country_not_country_level:
    'A guide country must be a country, or a place with "Show as a country" turned on.',

  // ── Alba médií ──
  album_name_required: "Album name is required.",
  album_parent_self: "An album cannot be nested under itself.",
  album_parent_descendant: "An album cannot be moved under its own sub-album.",
  album_nothing_to_update: "Nothing to update.",
  album_no_media: "No media selected.",

  // ── Recenze od klientů (vize F3.7) ──
  review_name_required: "A review without the customer's name cannot be published.",

  // ── Recenze na Googlu: import exportu Takeout (vize F2.1–F2.5) ──
  google_import_invalid:
    "The Google Takeout data is invalid. Upload the export again, exactly as Google Takeout downloaded it.",
  google_import_no_location:
    "The upload does not say which business location it is for. Choose the location and try again.",
  google_import_too_many: "The export contains too many reviews to import at once.",
  google_import_translation_only:
    "A review text contains Google's machine translation instead of the author's original wording. Upload the export again.",

  // ── Recenze na Googlu: správa (vize F2.3b, F2.6, F2.7, F2.9) ──
  google_review_invalid: "The Google review data is invalid.",
  google_review_not_manual:
    "Only manually added Google reviews can be edited or deleted. An imported review can only get a different destination or be removed permanently.",
  google_review_removed:
    "This Google review has been removed permanently and can no longer be changed.",
  google_review_date_invalid: "Enter a valid review date that is not in the future.",
  google_review_author_required: "Author name is required.",
  google_review_text_required: "Review text is required.",
  google_review_destination_not_found:
    "The selected destination does not exist. Reload the page and choose another one.",
  google_review_stars_not_allowed:
    "Stars are not entered: only five-star Google reviews are stored.",

  // ── Vazby zájezd ↔ inspirace (vize C5 / F6.1) ──
  trip_link_trip_unavailable:
    "Only published trips can be linked to an inspiration. The trip does not exist or is not published.",
  trip_link_inspiration_not_found: "The inspiration no longer exists. Reload the page.",
  trip_link_overlap: "The same item cannot be added and removed at once.",
} as const satisfies Record<string, string>;

export type ApiErrorCode = keyof typeof API_ERROR_MESSAGES;

/**
 * Kódy, kterými server odmítne SOUBOR 400 (trvalá chyba v panelu dávky, opakování nepomůže).
 * Jeden zdroj pravdy: odvozuje se z nich `ImageRejectionCode` API (`services/storage.ts`)
 * a admin podle nich třídí odpověď nahrání; lokální kopii seznamu nikdo nedrží.
 */
export const MEDIA_FILE_REJECTION_CODES = [
  "media_too_large",
  "media_type_not_allowed",
  "media_svg_unsafe",
  "media_content_invalid",
  "media_mime_mismatch",
  "media_exceeds_image_limits",
] as const satisfies readonly ApiErrorCode[];

export type MediaFileRejectionCode = (typeof MEDIA_FILE_REJECTION_CODES)[number];

/** Hodnoty parametrů šablony (`{{jmeno}}`). */
export type ApiErrorParams = Record<string, string | number>;

/**
 * `kód → týž kód`. Jediný povolený tvar textu Zod issue v katalogových schématech:
 * `message: API_ERROR.slug_format`, `.min(1, API_ERROR.team_member_name_required)`. Překlep
 * v kódu shodí typecheck; tvar hlídá měřidlo (pravidlo C).
 */
export const API_ERROR = Object.fromEntries(
  Object.keys(API_ERROR_MESSAGES).map((code) => [code, code]),
) as { [K in ApiErrorCode]: K };

export function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return (
    typeof value === "string" && Object.prototype.hasOwnProperty.call(API_ERROR_MESSAGES, value)
  );
}

/** Dosadí `{{jmeno}}` (syntaxe i18next); chybějící parametr je prázdný řetězec. */
export function interpolateApiErrorTemplate(template: string, params: ApiErrorParams = {}): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : "",
  );
}

/** Anglický text kódu s dosazenými parametry — `message` odpovědi API. */
export function renderApiErrorMessage(code: ApiErrorCode, params?: ApiErrorParams): string {
  return interpolateApiErrorTemplate(API_ERROR_MESSAGES[code], params);
}
